import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const DISMISS_PREFIX = "fy-chat-hint-dismissed";

function readDismissed(lang: string) {
  try {
    return sessionStorage.getItem(`${DISMISS_PREFIX}:${lang}`) === "1";
  } catch {
    return false;
  }
}

/**
 * Small bilingual nudge that sits above the floating assistant button.
 * It follows the active site language: the copy is written for the language
 * being read, and dismissing it only applies to that language, so switching
 * ES <-> EN brings it back in the new language.
 */
export default function ChatHint() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "es";
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);

  useEffect(() => {
    setVisible(false);
    if (readDismissed(lang)) {
      setClosed(true);
      return undefined;
    }
    setClosed(false);
    const timer = window.setTimeout(() => setVisible(true), 4500);
    return () => window.clearTimeout(timer);
  }, [lang]);

  const dismiss = () => {
    setVisible(false);
    setClosed(true);
    try {
      sessionStorage.setItem(`${DISMISS_PREFIX}:${lang}`, "1");
    } catch {
      /* ignore */
    }
  };

  const openChat = () => {
    const button = document.querySelector<HTMLElement>("#vapi-widget-floating-button");
    if (button) button.click();
    dismiss();
  };

  if (closed) return null;

  return (
    <div className={`chat-hint ${visible ? "is-in" : ""}`} data-lang={lang} lang={lang}>
      <button type="button" className="chat-hint-main" onClick={openChat}>
        <i className="ri-chat-smile-2-line" aria-hidden="true" />
        <span>{t("chat.hint")}</span>
      </button>
      <button type="button" className="chat-hint-x" onClick={dismiss} aria-label={t("chat.hintClose")}>
        <i className="ri-close-line" aria-hidden="true" />
      </button>
    </div>
  );
}